import {
  ArrowForwardRounded,
  ChecklistRtlRounded,
  DescriptionRounded,
  EditNoteRounded,
  MailOutlineRounded,
  MarkEmailUnreadRounded,
  NoteAddRounded,
  ReceiptLongRounded,
  SwapHorizRounded,
} from "@mui/icons-material";
import { ExecutionStep } from "../types/AIChat/ExecutionStep";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import { translate } from "./Translations/translatorService";
import { DrawerState } from "../types/DrawerState";
import { UpdateResourceParams } from "./ReactQuery/reactQueryService";
import { UseMutationResult } from "@tanstack/react-query";

export const getExecutionStepIcon = (actionType: ExecutionStep["actionType"]) => {
  const iconByActionType: Record<ExecutionStep["actionType"], OverridableComponent<SvgIconTypeMap>> = {
    createActivity: ChecklistRtlRounded,
    changeStatus: ArrowForwardRounded,
    createInquiryToCustomer: MarkEmailUnreadRounded,
    sendCustomerMessage: MailOutlineRounded,
    sendEmail: MailOutlineRounded,
    sendBeaOrFax: MailOutlineRounded,
    createNote: NoteAddRounded,
    updateField: EditNoteRounded,
    createDocument: DescriptionRounded,
    createBookingEntry: ReceiptLongRounded,
    createWireTransfer: SwapHorizRounded,
  };

  return iconByActionType[actionType] || DescriptionRounded;
};

export const getExecutionStepDescription = (step: ExecutionStep): string => {
  const payload = step.payload;

  switch (step.actionType) {
    case "sendCustomerMessage":
      return String(getMessagePayloadSubject(payload) || "Kundennachricht vorbereitet");
    case "sendEmail":
      return String(payload.toMail || payload.subject || "E-Mail vorbereitet");
    case "createActivity":
      return String(payload.subject || payload.type || "Aktivität vorbereitet");
    case "changeStatus":
      return String(payload.status || payload.nextStatus || "Statusänderung vorbereitet");
    case "createInquiryToCustomer":
      return String(payload.note || payload.subject || "Anfrage an Mandantschaft vorbereitet");
    case "updateField":
      return String(payload.path || payload.field || "Feldänderung vorbereitet");
    case "createNote":
      return String(payload.content || "Notiz vorbereitet")
        .replace(/<[^>]+>/g, "")
        .slice(0, 120);
    case "sendBeaOrFax":
      return String(
        payload.bea?.subject || payload.bea?.externalReference || payload.fax?.fax || "beA-Nachricht/Fax vorbereitet"
      );
    case "createDocument":
      return String(payload.title || payload.documentType || "Dokument vorbereitet");
    case "createBookingEntry":
      return String(payload.purpose || "Buchungseintrag vorbereitet");
    case "createWireTransfer":
      return String(payload.iban || payload.reference || "Überweisung vorbereitet");
    default:
      return translate(`executionJob.step.actionType.values.${step.actionType}`);
  }
};

const getMessagePayloadSubject = (payload: Record<string, any>): string | undefined => {
  if (typeof payload.conversation === "object" && payload.conversation?.subject) {
    return payload.conversation.subject;
  }

  return payload.subject;
};

export const getExecutionStepEditDrawerConfig = (
  step: ExecutionStep,
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>
): DrawerState | null => {
  const drawerBaseData = {
    overwrittenHandleSave: async (draftPayload: ExecutionStep["payload"]) => {
      await updateMutation.mutateAsync({
        uri: "execution_steps",
        id: step.id,
        data: {
          //lastEditedBy keeps user autosaves from remounting the open form (see useCaseRelatedDrawerConfig)
          payload: { ...draftPayload, lastEditedBy: "user" },
        },
      });
    },
    highlighted: true,
  };

  //step drawers carry no entityData snapshot: the forms read the live payload from the
  //ai_chat_messages cache via useCaseRelatedDrawerConfig (top-down data flow V2)
  switch (step.actionType) {
    case "createActivity":
      return {
        type: "activity",
        ...drawerBaseData,
      };
    case "createNote":
      return {
        type: "note",
        ...drawerBaseData,
      };
    case "sendCustomerMessage":
      return {
        type: "message",
        typeSpecificData: {
          customerCommunicationOnly: true, //recipient is structurally fixed to the case customer by the backend sendCustomerMessage contract
        },
        ...drawerBaseData,
      };
    case "sendBeaOrFax":
      return {
        type: "beaFax",
        ...drawerBaseData,
      };
    case "createInquiryToCustomer":
      return {
        type: "inquiryToCustomer",
        ...drawerBaseData,
      };
    case "createDocument":
      return {
        type: "documentEditor",
        wide: true,
        ...drawerBaseData,
      };
    case "sendEmail":
      return {
        type: "email",
        ...drawerBaseData,
      };
    case "changeStatus":
    case "updateField":
    case "createBookingEntry":
    case "createWireTransfer":
      return null;
    default:
      return null;
  }
};
